import { ChangeDetectionStrategy, Component, ElementRef, Host } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { GasClickOutsideDirective } from '../../utils/click-outside-directive/gas-click-outside.directive';
import { GasMenu } from './gas-menu.namespace';
import { GasMenuService } from './gas-menu.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'gas-menu',
  templateUrl: './gas-menu.component.html',
  styleUrls: ['./gas-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GasClickOutsideDirective,
  ],
})
export class GasMenuComponent {
  public meta$: Observable<GasMenu.Meta> = this.gasMenuService.meta$;
  public bottomButtons$: Observable<GasMenu.Bottom[]> = this.gasMenuService.bottomButtons$;
  public userData$: Observable<GasMenu.User> = this.gasMenuService.userData$;
  public activeRoute$: Observable<string> = this.gasMenuService.activeRoute$;
  public expandedChild$ = new BehaviorSubject<Record<string, boolean>>({});

  public isExpandedSubject$ = new BehaviorSubject<boolean>(false);
  public isExpanded$ = this.isExpandedSubject$.pipe(
    tap((isExpanded: boolean) => {
      const element: HTMLElement = this.elementRef.nativeElement;

      if (isExpanded) {
        element.classList.add('_expanded');
      } else {
        element.classList.remove('_expanded');
      }
    }),
  );

  constructor(
    @Host() public gasClickOutsideDirective: GasClickOutsideDirective,
    private gasMenuService: GasMenuService,
    private elementRef: ElementRef,
  ) {
    this.gasClickOutsideDirective.handleClick$
      .pipe(
        untilDestroyed(this),
      ).subscribe(() => this.handleOutside());
  }

  public toggleExpanded(): void {
    this.isExpandedSubject$.next(!this.isExpandedSubject$.value);
  }

  public handleOutside(): void {
    if (this.isExpandedSubject$.value) {
      this.toggleExpanded();
    }
  }

  public executeBottomButton(button: GasMenu.Bottom): void {
    if (button.hideMenuWhenClicked) {
      this.toggleExpanded();
    }

    button.callback();
  }

  public isExpandedRoute(route: GasMenu.Route): boolean {
    return this.expandedChild$.value[route.route];
  }

  public isActiveRoute(route: GasMenu.Route, activeRouteUrl: string): boolean {
    return (activeRouteUrl === '/' && route.route === '/')
      || this.gasMenuService.isActiveRoute(route.route)
      || (this.hasActiveInRoute(route, activeRouteUrl)
        ? this.isPartialChildRoute(route, activeRouteUrl)
        : this.isExactRoute(route, activeRouteUrl))
      || route.active;
  }

  public toggleChild(url: string): void {
    const expanded = this.expandedChild$.value;

    if (expanded[url]) {
      expanded[url] = !expanded[url];
    } else {
      expanded[url] = true;
    }

    this.expandedChild$.next(expanded);
  }

  public checkSelectedItem(route: GasMenu.Route): void {
    if (!route.childs?.length) {
      route.callback();
      this.toggleExpanded();
    }
  }

  /** TODO: Доработать рекурсию, чтоб можно было искать активных в глубине более чем на одного ребенка */
  private hasActiveInRoute(route: GasMenu.Route, activeRouteUrl: string): boolean {
    if (!route.childs) {
      return false;
    }

    const hasActiveRoute = route.childs.find((innerRoute: GasMenu.Route) => innerRoute.route === activeRouteUrl) !== undefined;

    if (hasActiveRoute) {
      return true;
    }

    return false;
  }

  private isExactRoute(route: GasMenu.Route, activeRouteUrl: string): boolean {
    return activeRouteUrl === route.route;
  }

  private isPartialChildRoute(route: GasMenu.Route, activeRouteUrl: string): boolean {
    return activeRouteUrl.indexOf(route.route) > -1 && route.route !== '/';
  }
}
